import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassesApi } from "@/openapi/generated";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  defaultPageSizeForPortal,
  getStudentAvailableClassesQueryKey,
  onlyAvailableOnlineForPortal,
  type StudentAvailableClassesPortal,
} from "./student-available-classes-query";

export type { StudentAvailableClassesPortal };

interface UseStudentAvailableClassesGetProps {
  studentId: number | null;
  seasonId?: number | null;
  locationId?: number | null;
  page?: number;
  pageSize?: number;
  lastSelectedId?: number;
  active?: boolean;
  /** Member portal filters to online-available classes; admin sees all eligible classes. */
  portal?: StudentAvailableClassesPortal;
  enabled?: boolean;
}

const MEMBER_STALE_TIME_MS = 5 * 60 * 1000;

export const useStudentAvailableClassesGet = ({
  studentId,
  seasonId = null,
  locationId = null,
  page = 1,
  pageSize,
  lastSelectedId,
  active,
  portal = "admin",
  enabled = true,
}: UseStudentAvailableClassesGetProps) => {
  const accessToken = useAccessToken();
  const resolvedPageSize = pageSize ?? defaultPageSizeForPortal(portal);
  const onlyAvailableOnline = onlyAvailableOnlineForPortal(portal);

  const queryResult = useQuery({
    queryKey: getStudentAvailableClassesQueryKey({
      studentId: studentId ?? 0,
      seasonId,
      locationId,
      page,
      pageSize: resolvedPageSize,
      lastSelectedId,
      active,
      portal,
    }),
    enabled: enabled && Boolean(studentId) && !!accessToken,
    refetchOnWindowFocus: false,
    staleTime: portal === "member" ? MEMBER_STALE_TIME_MS : undefined,
    refetchOnMount: portal === "member" ? false : undefined,
    queryFn: async () => {
      if (!studentId) {
        return null;
      }
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(ClassesApi, accessToken);
      return service.apiClassesEnrollmentAvailablePost({
        page,
        pageSize: resolvedPageSize,
        lastSelectedId,
        active,
        getAvailableClassesRequest: {
          studentId,
          seasonId: seasonId ?? undefined,
          locationId: locationId ?? undefined,
          onlyAvailableOnline,
        },
      });
    },
  });

  return queryResult;
};
